/* Content for the five product pages.
   The three delivery pathways come from the deployment strategy deck; the
   BenchmarkIQ page from the product concept deck. Only the customer-facing
   parts of that deck are here — pricing, go-to-market and revenue strategy
   are deliberately left out, since that deck is marked confidential. */

// Shared by the three pathway pages: the same question answered three ways.
export const PATHWAY_COMPARISON = {
  intro:
    'The same platform, delivered three ways. The choice turns on one question — where your data is allowed to live.',
  columns: ['Desktop', 'Enterprise Managed', 'Enterprise Native'],
  badges: ['Standardised', 'AnalyticaX hosted', 'Client hosted'],
  rows: [
    {
      label: 'Ideal profile',
      values: [
        'Rapid insights using standardised reporting.',
        'Custom embedded analytics without internal infrastructure overhead.',
        'Strict data governance requiring zero external data movement.'
      ]
    },
    {
      label: 'Where data lives',
      values: ['Uploaded to AnalyticaX Web', 'AnalyticaX Fabric servers', 'Your own servers']
    },
    {
      label: 'How it works',
      values: [
        'Self-serve upload, then a pre-designed dashboard.',
        'Data exported to AnalyticaX using cloud credentials you provide.',
        'AnalyticaX works inside your perimeter; data never leaves it.'
      ]
    },
    {
      label: 'What you get',
      values: [
        'Web-based views of pre-designed templates.',
        'App registration for Power BI embed, with a URL of your own.',
        'A native interface built into your architecture, with Power BI embedded.'
      ]
    },
    {
      label: 'To begin',
      values: [
        'Download the templates and upload your data.',
        'Provide secure cloud access; we start the pipeline.',
        'Grant workspace access; we build inside your ecosystem.'
      ]
    }
  ],
  paths: [
    '/products/desktop-analytics',
    '/products/enterprise-managed-analytics',
    '/products/enterprise-native-analytics'
  ]
};

export const PRODUCTS = {
  'one-rupee-erp': {
    eyebrow: 'The platform',
    name: 'One Rupee ERP',
    tagline: 'The analytics layer ERPs never built',
    intro:
      'A Power BI-native platform for schools: onboard data your way, model it properly, ask questions in plain English, and see where you stand against comparable institutions.',
    facts: [
      { label: 'Built on', value: 'Microsoft Power BI and Fabric' },
      { label: 'Delivered through', value: 'Three infrastructure pathways' },
      { label: 'Works with', value: 'Your existing ERP' }
    ],
    capabilities: {
      title: 'What it does',
      items: [
        {
          title: 'Onboard data your way',
          body: 'Start from a ready-made template, or design your own structure. Nothing forces your data into a shape it does not have.'
        },
        {
          title: 'Real semantic modeling',
          body: 'Measures built to match how your institution actually tracks performance, defined once so every report agrees with every other.'
        },
        {
          title: 'Reports built for you',
          body: 'Native dashboards from day one, or custom reports built to your specification.'
        },
        {
          title: 'Ask it in plain English',
          body: 'A data agent answers questions from the semantic model, so an answer and a dashboard cannot disagree.'
        },
        {
          title: 'Benchmarks no ERP offers',
          body: 'Peer comparison across schools by size, region and affiliation — the context your own numbers cannot give you.'
        },
        {
          title: 'Your own visuals, natively',
          body: 'Charts drawn from your templates directly, beside the Power BI reports, with filters and export built in.'
        }
      ]
    },
    steps: {
      title: 'Getting started',
      items: [
        { title: 'Choose a pathway', body: 'Desktop, Enterprise Managed or Enterprise Native — the decision rests on where your data is allowed to live.' },
        { title: 'Bring your data', body: 'Upload against a template, or let us connect to your ERP directly.' },
        { title: 'Model it once', body: 'We define the measures with you, so every report is built on the same definitions.' },
        { title: 'Read it anywhere', body: 'Dashboards, native visuals, plain-English answers and peer benchmarks, from the same foundation.' }
      ]
    },
    showComparison: true,
    related: ['desktop-analytics', 'enterprise-managed-analytics', 'enterprise-native-analytics', 'benchmarks']
  },

  'desktop-analytics': {
    eyebrow: 'Delivery pathway 1',
    name: 'Desktop Analytics',
    tagline: 'Standardised',
    intro:
      'Maximised convenience and speed. Ideal for rapid insights using standardised reporting, delivered as web-based views of pre-designed templates.',
    facts: [
      { label: 'Where data lives', value: 'Uploaded to AnalyticaX Web' },
      { label: 'Engagement', value: 'Self-serve' },
      { label: 'Time to first dashboard', value: 'Immediate' }
    ],
    flow: {
      title: 'Where your data goes',
      steps: [
        { icon: 'Building2', title: 'Your institution', body: 'Your existing exports, in the shape the template expects.' },
        { icon: 'UploadCloud', title: 'AnalyticaX Web', body: 'Uploaded through the website. Nothing to provision.' },
        { icon: 'LayoutDashboard', title: 'Your dashboard', body: 'Web-based views of the pre-designed reports.' }
      ]
    },
    decision: {
      quote: 'Data residency is flexible; I need immediate dashboard access.',
      action: 'Access website templates',
      body: 'Download the standardised templates and upload your data straight away.'
    },
    capabilities: {
      title: 'What you get',
      items: [
        {
          title: 'Pre-built templates',
          body: 'Ready-to-use Power BI report templates you populate with your own data. The fastest path to analytics there is.'
        },
        {
          title: 'No infrastructure',
          body: 'Nothing to provision, nothing to host, no credentials to hand over. Upload and read.'
        },
        {
          title: 'Standard KPI set',
          body: 'The measures schools ask for most, already defined — attendance, collections, admissions and academic performance.'
        },
        {
          title: 'A path onward',
          body: 'Start here and move to a managed or native deployment later without redoing the modelling.'
        }
      ]
    },
    steps: {
      title: 'How it works',
      items: [
        { title: 'Download a template', body: 'Pick the template for what you want to measure.' },
        { title: 'Fill it with your data', body: 'Your existing exports, in the shape the template expects.' },
        { title: 'Upload it', body: 'Straight through the website, no intermediary.' },
        { title: 'Read your dashboard', body: 'Web-based views of the pre-designed reports, ready immediately.' }
      ]
    },
    audience: 'Schools new to data analytics who want immediate insight, and anyone whose data residency requirements are flexible.',
    showComparison: true,
    related: ['one-rupee-erp', 'enterprise-managed-analytics', 'enterprise-native-analytics']
  },

  'enterprise-managed-analytics': {
    eyebrow: 'Delivery pathway 2',
    name: 'Enterprise Managed Analytics',
    tagline: 'AnalyticaX hosted',
    intro:
      'Custom embedded analytics without internal infrastructure overhead. We host the Fabric infrastructure; you get an app registration for Power BI embed with a URL of your own.',
    facts: [
      { label: 'Where data lives', value: 'AnalyticaX Fabric servers' },
      { label: 'Engagement', value: 'ERP-led' },
      { label: 'You provide', value: 'Secure cloud credentials' }
    ],
    flow: {
      title: 'Where your data goes',
      steps: [
        { icon: 'Building2', title: 'Your institution', body: 'Scoped cloud credentials you provide.' },
        { icon: 'Server', title: 'AnalyticaX Fabric', body: 'Exported and modelled on infrastructure we run.' },
        { icon: 'Code2', title: 'Embedded in your app', body: 'Power BI embed on a URL of your own.' }
      ]
    },
    decision: {
      quote: 'We need custom embedded reporting, but prefer AnalyticaX to host the Fabric infrastructure.',
      action: 'Provision cloud credentials',
      body: 'Give us secure cloud access and we start generating the pipeline.'
    },
    capabilities: {
      title: 'What you get',
      items: [
        {
          title: 'Custom analytics, no infrastructure',
          body: 'Reports built to your specification, running on infrastructure you never have to buy, size or maintain.'
        },
        {
          title: 'Embedded in your product',
          body: 'An app registration for Power BI embed and a client-specific URL, so the reports live inside your own application.'
        },
        {
          title: 'Analytics across a school base',
          body: 'For ERPs: send data on behalf of your schools and we generate analytics for all of them, or a selected set.'
        },
        {
          title: 'Maintained for you',
          body: 'Pipelines, refreshes and the semantic model are ours to keep running.'
        }
      ]
    },
    steps: {
      title: 'How it works',
      items: [
        { title: 'Provide cloud credentials', body: 'Secure, scoped access to the data we need and nothing more.' },
        { title: 'We build the pipeline', body: 'Your data is exported to AnalyticaX and loaded into Microsoft Fabric.' },
        { title: 'We model and build', body: 'The semantic model and the reports, to your specification.' },
        { title: 'Embed it', body: 'App registration plus a client-specific URL, dropped into your own product.' }
      ]
    },
    audience: 'ERPs wanting to offer analytics across their school base, and institutions wanting custom embedded reporting without running the infrastructure.',
    showComparison: true,
    related: ['one-rupee-erp', 'desktop-analytics', 'enterprise-native-analytics']
  },

  'enterprise-native-analytics': {
    eyebrow: 'Delivery pathway 3',
    name: 'Enterprise Native Analytics',
    tagline: 'Client hosted',
    intro:
      'Maximised data privacy and customisation, for strict data governance requiring zero external data movement. We work inside your perimeter and your data never leaves it.',
    facts: [
      { label: 'Where data lives', value: 'Your own servers' },
      { label: 'Engagement', value: 'Full-service' },
      { label: 'Data movement', value: 'None' }
    ],
    flow: {
      title: 'Where your data goes',
      steps: [
        { icon: 'Building2', title: 'Your institution', body: 'Workspace access, under your own controls.' },
        { icon: 'ShieldCheck', title: 'Your own servers', body: 'Nothing is exported. The data never crosses your perimeter.' },
        { icon: 'MonitorCog', title: 'Native interface', body: 'Built into your architecture, with Power BI embedded.' }
      ]
    },
    decision: {
      quote: 'Data cannot leave our internal servers under any circumstances.',
      action: 'Grant workspace access',
      body: 'Provision AnalyticaX access to your native ecosystem and we build the pipelines inside it.'
    },
    capabilities: {
      title: 'What you get',
      items: [
        {
          title: 'Zero external data movement',
          body: 'Nothing is exported. We work within your architecture, which is what makes this viable under strict governance.'
        },
        {
          title: 'A native interface',
          body: 'User interface development inside your own application, with Power BI embedded into it.'
        },
        {
          title: 'An always-on analytics layer',
          body: 'For large ERPs and school groups: we access your data directly, load it into Microsoft Fabric within your estate, and maintain the visuals.'
        },
        {
          title: 'Customisation without limit',
          body: 'Because it is built into your architecture rather than beside it, there is no boundary to work around.'
        }
      ]
    },
    steps: {
      title: 'How it works',
      items: [
        { title: 'Grant workspace access', body: 'Scoped access into your own ecosystem, under your own controls.' },
        { title: 'We enter the perimeter', body: 'Pipelines are built where your data already sits.' },
        { title: 'Native build', body: 'Interface development within your architecture, with Power BI embedded.' },
        { title: 'Ongoing maintenance', body: 'We keep the models and the visuals current, inside your estate.' }
      ]
    },
    audience: 'Large ERPs and school groups wanting an always-on analytics layer, and any institution whose data is not permitted to leave its own servers.',
    showComparison: true,
    related: ['one-rupee-erp', 'desktop-analytics', 'enterprise-managed-analytics']
  },

  benchmarks: {
    eyebrow: 'Launching soon',
    name: 'BenchmarkIQ',
    tagline: 'Collective intelligence for schools',
    intro:
      "India's first collective intelligence platform for K-12 school performance benchmarking. It aggregates anonymised KPI data from schools across the country, generates statistically rigorous benchmarks, and delivers peer-comparison intelligence to school leadership.",
    facts: [
      { label: 'Status', value: 'Launching soon' },
      { label: 'Minimum cohort', value: 'n ≥ 10 schools' },
      { label: 'Designed for', value: "India's DPDPA framework" }
    ],
    problem: {
      title: 'Every school is flying blind',
      items: [
        {
          title: 'No external reference',
          body: 'Schools track their own KPIs but have no way to know whether their fee collection rate, attendance or exam scores are good or poor relative to peers.'
        },
        {
          title: 'The data exists, but siloed',
          body: 'ERPs hold performance data for hundreds of schools. Nobody aggregates it into insight.'
        },
        {
          title: 'AnalyticaX is positioned to build it',
          body: 'With existing ERP relationships, enterprise data access and Power BI infrastructure, we can build what no one else can.'
        }
      ]
    },
    capabilities: {
      title: 'Three pillars',
      items: [
        { title: 'Contribute', body: 'Schools share data through their existing ERP or through AnalyticaX pipelines.' },
        { title: 'Anonymise', body: 'A privacy engine ensures no school-level data is ever exposed.' },
        { title: 'Benchmark', body: 'Aggregated cohort intelligence, delivered as reports, dashboards and alerts.' }
      ]
    },
    kpis: {
      title: 'What gets benchmarked',
      items: [
        { title: 'Financial health', body: 'Fee collection rate · days fees delayed · receivables trend · payment mode split' },
        { title: 'Academic pulse', body: 'Average scores by subject and class · board exam percentiles · remedial uptake' },
        { title: 'Operational health', body: 'Attendance rate · teacher absenteeism · staff turnover · infrastructure utilisation' },
        { title: 'Admissions and retention', body: 'Enrolment against target · mid-year attrition · enquiry-to-admission conversion' }
      ]
    },
    privacy: {
      title: 'Privacy is a first-class feature',
      intro:
        'Built on trust. Every benchmark is engineered so that no single school can be identified or reverse-engineered.',
      items: [
        { title: 'Minimum cohort rule', body: 'Benchmarks publish only when at least 10 schools contribute to that segment.' },
        { title: 'Contribution is not visibility', body: 'Contributing data and purchasing benchmark reports are separate decisions.' },
        { title: 'Opt-out registry', body: 'Schools can withdraw from data contribution at any time, processed within 30 days.' },
        { title: 'Noise injection and k-anonymity', body: "Statistical techniques prevent back-calculation of any individual school's data." },
        { title: 'Annual purge rights', body: 'Schools can request deletion of their identified contribution records each year.' }
      ],
      note: "Designed for compliance with India's DPDPA framework."
    },
    segmentation: {
      title: 'Benchmarks relevant to you',
      intro: "Every insight is filtered by your school's context, not a generic national average.",
      rows: [
        { label: 'School size', value: 'Micro (under 200) · Small (200–600) · Mid (600–1,500) · Large (1,500+)' },
        { label: 'Board affiliation', value: 'CBSE · ICSE · IB · State board' },
        { label: 'Region', value: 'Tier 1 city · Tier 2 city · Semi-urban · Rural' },
        { label: 'School type', value: 'Day · Boarding · Co-ed · Single-gender' },
        { label: 'Fee bracket', value: 'Budget · Mid-range · Premium' },
        { label: 'Curriculum focus', value: 'General · STEM-heavy · Arts · Sports' }
      ],
      example: 'Show me fee collection rate benchmarks for CBSE schools in Tier 2 cities with 400–800 students.'
    },
    audience: 'Schools and ERPs wanting external context to evaluate their own performance.',
    showComparison: false,
    related: ['one-rupee-erp', 'desktop-analytics', 'enterprise-managed-analytics', 'enterprise-native-analytics']
  }
};

export const PRODUCT_ICONS = {
  'one-rupee-erp': 'IndianRupee',
  'desktop-analytics': 'Monitor',
  'enterprise-managed-analytics': 'Cloud',
  'enterprise-native-analytics': 'Lock',
  benchmarks: 'BarChart3'
};

export const PRODUCT_INDEX = Object.entries(PRODUCTS).map(([slug, product]) => ({
  slug,
  name: product.name,
  tagline: product.tagline,
  icon: PRODUCT_ICONS[slug]
}));
